import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faShieldAlt } from "@fortawesome/free-solid-svg-icons";
import { QRCodeSVG } from "qrcode.react";
import {
  get2FAStatus,
  enroll2FA,
  confirm2FA,
  disable2FA,
  set2FALoginRequired,
} from "../../store/actions";

const TwoFactorSection = () => {
  const dispatch = useDispatch();
  const twoFactor = useSelector((s) => s.userReducer.twoFactor);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    dispatch(get2FAStatus());
  }, [dispatch]);

  const clearNotices = () => {
    setErr("");
    setMsg("");
  };

  const startSetup = async () => {
    clearNotices();
    setBusy(true);
    try {
      const res = await dispatch(enroll2FA());
      setSetup({ secret: res?.secret, otpauthUri: res?.otpauthUri });
    } catch (e) {
      setErr(e?.message || "Failed to start 2FA setup.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!/^[0-9]{6}$/.test((code || "").trim())) {
      setErr("Enter the 6-digit code from your authenticator app.");
      return;
    }
    clearNotices();
    setBusy(true);
    try {
      await dispatch(confirm2FA(code.trim()));
      setSetup(null);
      setCode("");
      setMsg("Two-factor authentication is now enabled.");
    } catch (e) {
      setErr(e?.message || "Failed to confirm the code.");
    } finally {
      setBusy(false);
    }
  };

  const doDisable = async () => {
    if (!/^[0-9]{6}$/.test((code || "").trim())) {
      setErr("Enter your current 6-digit code to disable 2FA.");
      return;
    }
    clearNotices();
    setBusy(true);
    try {
      await dispatch(disable2FA(code.trim()));
      setCode("");
      setMsg("Two-factor authentication disabled.");
    } catch (e) {
      setErr(e?.message || "Failed to disable 2FA.");
    } finally {
      setBusy(false);
    }
  };

  const toggleLogin = async () => {
    if (!/^[0-9]{6}$/.test((code || "").trim())) {
      setErr("Enter your current 6-digit code to change this setting.");
      return;
    }
    clearNotices();
    setBusy(true);
    try {
      await dispatch(set2FALoginRequired(!twoFactor.loginRequired, code.trim()));
      setCode("");
      setMsg("Login 2FA setting updated.");
    } catch (e) {
      setErr(e?.message || "Failed to update the login 2FA setting.");
    } finally {
      setBusy(false);
    }
  };

  const codeInput = (placeholder) => (
    <input
      className="input mono"
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      placeholder={placeholder}
      value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
      style={{ maxWidth: 160, textAlign: "center" }}
    />
  );

  return (
    <div className="panel" style={{ maxWidth: 900, marginBottom: 24, padding: 18 }}>
      <h3
        className="secH"
        style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 10 }}
      >
        <FontAwesomeIcon icon={faShieldAlt} /> Two-factor authentication (2FA)
      </h3>
      <p className="secLead" style={{ marginTop: 0 }}>
        2FA is required to change your withdrawal wallet and to request a
        withdrawal. You can also require it at login.
      </p>

      {!twoFactor.enabled && !setup && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="notice">
            <span className="ni">⚠</span>
            <div>
              You have not set up 2FA yet. Withdrawal wallet changes and
              withdrawals are blocked until you do.
            </div>
          </div>
          <button
            type="button"
            className="btnPrimary"
            onClick={startSetup}
            disabled={busy}
          >
            {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : "Set up 2FA"}
          </button>
        </div>
      )}

      {setup && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label className="fldLbl">
              1. Add this key to your authenticator app
            </label>
            {setup.otpauthUri && (
              <div
                style={{
                  background: "#fff",
                  padding: 12,
                  borderRadius: 10,
                  width: "fit-content",
                  margin: "0 0 12px",
                }}
              >
                <QRCodeSVG value={setup.otpauthUri} size={168} level="M" />
              </div>
            )}
            <div
              className="lwAddrBox"
              style={{ margin: "0 0 6px", wordBreak: "break-all" }}
            >
              {setup.secret}
            </div>
            <div className="fldHelp">
              Scan the QR with Google Authenticator, Authy or a similar app, or
              choose "Enter a setup key" and paste the key above.
            </div>
          </div>
          <div>
            <label className="fldLbl">2. Enter the 6-digit code to confirm</label>
            {codeInput("123456")}
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              type="button"
              className="btnPrimary"
              onClick={confirm}
              disabled={busy}
            >
              {busy ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                "Confirm & enable"
              )}
            </button>
            <button
              type="button"
              className="btnGhost"
              onClick={() => {
                setSetup(null);
                setCode("");
                clearNotices();
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {twoFactor.enabled && !setup && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="notice green">
            <span className="ni">✓</span>
            <div>
              Two-factor authentication is <b>on</b>
              {twoFactor.enrolledAt ? ` (since ${twoFactor.enrolledAt})` : ""}.
            </div>
          </div>
          <div>
            <label className="fldLbl">Require 2FA at login</label>
            <div className="fldHelp">
              Currently <b>{twoFactor.loginRequired ? "enabled" : "disabled"}</b>.
              Changing 2FA settings requires your current code.
            </div>
          </div>
          <div>
            <label className="fldLbl">Current 6-digit code</label>
            {codeInput("123456")}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btnPrimary"
              onClick={toggleLogin}
              disabled={busy}
            >
              {busy ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : twoFactor.loginRequired ? (
                "Disable login 2FA"
              ) : (
                "Enable login 2FA"
              )}
            </button>
            <button
              type="button"
              className="btnGhost"
              onClick={doDisable}
              disabled={busy}
            >
              Disable 2FA
            </button>
          </div>
        </div>
      )}

      {err && (
        <div className="notice red" style={{ marginTop: 12 }}>
          <span className="ni">⚠</span>
          <div>{err}</div>
        </div>
      )}
      {msg && (
        <div className="notice green" style={{ marginTop: 12 }}>
          <span className="ni">✓</span>
          <div>{msg}</div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSection;
